import type { Character, CharacterStats } from '@/data/characters';
import type { CreatorVerseRow } from '@/lib/localCreatorStore';

export type AdaptedCreatorVerse = {
  key: string;
  label: string;
  backdrop: string;
  roles: string[];
  characters: Character[];
};

function clampStat(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1000;
  return Math.max(1000, Math.min(9999, Math.round(parsed)));
}

function slug(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function readRoleValue(raw: unknown, roleName: string) {
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    return clampStat(record[roleName] ?? Object.values(record)[0]);
  }
  return clampStat(raw);
}

export function creatorVerseLabel(row: CreatorVerseRow) {
  return `Creator: ${row.verse_name}`;
}

export function adaptCreatorVerse(row: CreatorVerseRow): AdaptedCreatorVerse {
  const label = creatorVerseLabel(row);
  const roles = (row.data_json.roles || []).map((role) => role.name).filter(Boolean);
  const roleIds = new Map((row.data_json.roles || []).map((role) => [role.id, role.name]));

  const characters = (row.data_json.characters || []).map((entry): Character => {
    const stats = Array.from(roleIds.entries()).reduce((acc, [roleId, roleName]) => {
      acc[roleName] = readRoleValue(entry.stats_by_role?.[roleId], roleName);
      return acc;
    }, {} as CharacterStats);

    return {
      id: `creator-${slug(row.id)}-${slug(entry.id || entry.name)}`,
      name: entry.name,
      verse: label,
      portrait: entry.image_url || row.data_json.meta.background_image_url || '/assets/logo.png',
      stats,
    };
  });

  return {
    key: `creator:${row.id}`,
    label,
    backdrop: row.data_json.meta.background_image_url || '/assets/themes/Logo Backdrop.webp',
    roles,
    characters,
  };
}
